/** Submit ceremony hook — handles answer submission, streaming grading phases, and sound effects. */
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import type {
  GradingPhaseEvent,
  StudySessionStep,
  StudySessionStepResponse,
} from '@formency/shared';
import { isAlreadyAnswered } from '@/shared/api/hooks';
import type { StepSubmitResponse, UnifiedStepResult } from '@/shared/api/hooks/useStepSubmit';
import { useSession } from '../SessionContext';
import { useSessionSound } from '../useSessionSound';
import type { StepAnswer, StepResult, CompletionMetrics } from '../types';

// ─── Types ──────────────────────────────────────────────────────────────────

interface UseSubmitCeremonyOptions {
  /** Submit function that sends the answer to the API and returns the grading result. */
  submitFn: (
    sessionId: string,
    position: number,
    answer: StepAnswer,
    onGradingPhase?: (event: GradingPhaseEvent) => void,
    responseTimeMs?: number,
    confidence?: number,
    behavioral?: {
      readingMs?: number;
      answeringMs?: number;
      revisionCount?: number;
      initialResponseJson?: unknown;
      focusLossCount?: number;
      idleCount?: number;
    },
    paasEffort?: number
  ) => Promise<{
    result: StepResult;
    response: unknown;
    raw: UnifiedStepResult;
  }>;
  /** Optional active-time provider from useStepTimer. When set, replaces the basic elapsed timer. */
  getActiveTimeMs?: () => number;
  /** Phase time provider from useStepTimer. Returns reading/answering split. */
  getPhaseTimesMs?: () => { readingMs: number; answeringMs: number };
  /** Behavioral metrics provider from useBehavioralTracker. */
  getBehavioralMetrics?: () => {
    revisionCount: number;
    initialResponseJson: unknown | null;
    focusLossCount: number;
    idleCount: number;
  };
}

interface UseSubmitCeremonyReturn {
  /** Call this when Check button is pressed (question steps) */
  submit: () => void;
  /** Call this when Continue is pressed (info steps) */
  submitInfoStep: () => void;
  /** True during beats 1-3 (commitment -> anticipation -> reveal) */
  isSubmitting: boolean;
  /** Set a pending confidence value to attach to the next submission. */
  setConfidence: (value: number | undefined) => void;
  /**
   * Set a pending Paas mental-effort value (1-9) to attach to the next
   * submission. Cleared after each submit. See spec §7.4.
   */
  setPaasEffort: (value: number | undefined) => void;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useSubmitCeremony(options: UseSubmitCeremonyOptions): UseSubmitCeremonyReturn {
  const { state, dispatch, currentStep } = useSession();
  const { playCorrect, playIncorrect } = useSessionSound();
  const submittingRef = useRef(false);
  const pendingConfidenceRef = useRef<number | undefined>(undefined);
  const pendingPaasEffortRef = useRef<number | undefined>(undefined);
  const soundEnabledRef = useRef(state.soundEnabled);
  const { submitFn, getActiveTimeMs, getPhaseTimesMs, getBehavioralMetrics } = options;

  // Fallback elapsed timer — only used when useStepTimer is not provided
  const stepStartRef = useRef(performance.now());
  const prevIndexRef = useRef(state.currentIndex);

  soundEnabledRef.current = state.soundEnabled;

  if (state.currentIndex !== prevIndexRef.current) {
    prevIndexRef.current = state.currentIndex;
    stepStartRef.current = performance.now();
  }

  const setConfidence = useCallback((value: number | undefined) => {
    pendingConfidenceRef.current = value;
  }, []);

  const setPaasEffort = useCallback((value: number | undefined) => {
    pendingPaasEffortRef.current = value;
  }, []);

  // ── Handle server responses (shared between submit and submitInfoStep) ────

  const handleAutoComplete = useCallback(
    (raw: UnifiedStepResult) => {
      if (!isAlreadyAnswered(raw) && (raw as StepSubmitResponse).sessionCompleted) {
        dispatch({
          type: 'SESSION_AUTO_COMPLETED',
          completionData: (raw as StepSubmitResponse).completionData as CompletionMetrics | null,
        });
      }
    },
    [dispatch]
  );

  // ── Question step submission ──────────────────────────────────────────────

  const submit = useCallback(() => {
    if (state.phase !== 'answering' || !state.answer || !currentStep) {
      return;
    }

    if (submittingRef.current) {
      return;
    }

    submittingRef.current = true;

    // Beat 1 — Commitment: dispatch SUBMIT (instant)
    dispatch({ type: 'SUBMIT' });

    const responseTimeMs = getActiveTimeMs
      ? getActiveTimeMs()
      : Math.round(performance.now() - stepStartRef.current);

    const run = async () => {
      try {
        const confidence = pendingConfidenceRef.current;
        pendingConfidenceRef.current = undefined;
        const paasEffort = pendingPaasEffortRef.current;
        pendingPaasEffortRef.current = undefined;

        const phaseTimes = getPhaseTimesMs?.();
        const behavioral = getBehavioralMetrics?.();

        const apiResult = await submitFn(
          state.sessionId,
          state.currentIndex,
          state.answer!,
          (event) => dispatch({ type: 'GRADING_PHASE', event }),
          responseTimeMs,
          confidence,
          {
            readingMs: phaseTimes?.readingMs,
            answeringMs: phaseTimes?.answeringMs,
            revisionCount: behavioral?.revisionCount,
            initialResponseJson: behavioral?.initialResponseJson,
            focusLossCount: behavioral?.focusLossCount,
            idleCount: behavioral?.idleCount,
          },
          paasEffort
        );

        // Handle already-answered (409) — sync session state and jump to where we left off
        if (isAlreadyAnswered(apiResult.raw)) {
          const session = apiResult.raw.session as { studySessionSteps?: StudySessionStep[] };
          const steps = session.studySessionSteps ?? [];
          if (steps.length > 0) {
            dispatch({ type: 'SYNC_SESSION', steps });
          }
          toast('This step was already answered — jumping to where you left off');
          return;
        }

        // Beat 3 — Reveal: dispatch result and play sound.
        //
        // Extract readiness fields from the raw response payload (set by the
        // server in submitStep — see spec §9.1 / §10.1). Drives the in-session
        // HUD and the break-modal gate.
        //
        // CONTRACT: this path only handles step-submit responses. Session-resume
        // score updates are produced server-side in getActiveSession via
        // applyRecovery and are NEVER dispatched through RECEIVE_RESULT.
        // The break-modal gate's "suppress on session-resume" rule (spec §11.1)
        // therefore holds by construction — the gate consumes state.readiness,
        // and state.readiness is only populated here. If a future refactor ever
        // wires session-resume scores into this dispatch path, the gate must be
        // explicitly updated to filter on trigger='step_submit'.
        const rawReadiness = (apiResult.raw as { readiness?: unknown }).readiness;
        const readiness =
          rawReadiness && typeof rawReadiness === 'object'
            ? (rawReadiness as {
                score: number;
                bars: 1 | 2 | 3 | 4 | 5;
                isColdStart: boolean;
                algorithmVersion: string;
                episodeResponseCount: number;
                episodeStartedAtMs: number;
                lastModalDismissedAtMs: number | null;
                activeMinutesInEpisode: number;
                activeMinutesInSession: number;
                sessionStartedAtMs: number;
              })
            : undefined;
        dispatch({
          type: 'RECEIVE_RESULT',
          result: apiResult.result,
          response: apiResult.response as StudySessionStepResponse,
          ...(readiness && { readiness }),
        });

        // Check for server-side auto-completion
        handleAutoComplete(apiResult.raw);

        if (soundEnabledRef.current) {
          if (apiResult.result.isCorrect) {
            playCorrect();
          } else {
            playIncorrect();
          }
        }
      } catch {
        // Reset to answering phase so the user can retry
        dispatch({ type: 'SUBMIT_ERROR' });
      } finally {
        submittingRef.current = false;
      }
    };

    void run();
  }, [
    state.phase,
    state.answer,
    state.sessionId,
    state.currentIndex,
    currentStep,
    dispatch,
    submitFn,
    getActiveTimeMs,
    getPhaseTimesMs,
    getBehavioralMetrics,
    handleAutoComplete,
    playCorrect,
    playIncorrect,
  ]);

  // ── Info step submission ──────────────────────────────────────────────────

  const submitInfoStep = useCallback(() => {
    if (state.phase !== 'idle' || currentStep?.stepKind !== 'info') {
      return;
    }

    if (submittingRef.current) {
      return;
    }

    const isLastStep = state.currentIndex === state.steps.length - 1;

    if (!isLastStep) {
      // Non-last info step: advance immediately, fire-and-forget POST.
      // Backend catch-up on the next blocking submit ensures any missed acks are recovered.
      //
      // submittingRef guards against keyboard+click double-dispatch WITHIN a
      // single event tick (synchronous re-entry before React re-renders). It
      // is reset SYNCHRONOUSLY after dispatch+POST initiation — not in the
      // POST's finally — because the user is now on a different step and a
      // fresh click there must NOT be blocked by the previous step's
      // in-flight background request. Two info steps in a row was
      // double-clicking otherwise: 1st click on step N+1 fires while step N's
      // POST is still in flight (1-2 s), submittingRef is still true →
      // silently dropped → user clicks again.
      submittingRef.current = true;
      dispatch({ type: 'NEXT_STEP' });
      void submitFn(
        state.sessionId,
        state.currentIndex,
        { type: 'info' },
        undefined,
        undefined
      ).catch(() => {
        // Swallow — catch-up on next blocking submit will fix this
      });
      submittingRef.current = false;
      return;
    }

    // Last step: blocking — must await so completion data is available
    submittingRef.current = true;

    // Transition to submitting phase
    dispatch({ type: 'SUBMIT_INFO' });

    const run = async () => {
      try {
        const apiResult = await submitFn(state.sessionId, state.currentIndex, { type: 'info' });

        // Handle already-answered (409) — sync session state and jump to where we left off
        if (isAlreadyAnswered(apiResult.raw)) {
          const session = apiResult.raw.session as { studySessionSteps?: StudySessionStep[] };
          const steps = session.studySessionSteps ?? [];
          if (steps.length > 0) {
            dispatch({ type: 'SYNC_SESSION', steps });
          }
          toast('This step was already answered — jumping to where you left off');
          return;
        }

        // Check for server-side auto-completion first
        handleAutoComplete(apiResult.raw);

        // Advance to next step
        dispatch({ type: 'NEXT_STEP' });
      } catch {
        dispatch({ type: 'SUBMIT_ERROR' });
      } finally {
        submittingRef.current = false;
      }
    };

    void run();
  }, [
    state.phase,
    state.sessionId,
    state.currentIndex,
    state.steps.length,
    currentStep,
    dispatch,
    submitFn,
    handleAutoComplete,
  ]);

  return {
    submit,
    submitInfoStep,
    isSubmitting: state.phase === 'submitting' || state.phase === 'grading',
    setConfidence,
    setPaasEffort,
  };
}
